"use client";

import {
  CopyButton,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { STANDARDIZED_MORTALITY_RATIO_CALCULATOR_STRINGS } from "@/components/tools/standardized-mortality-ratio-calculator";
import {
  DataGrid,
  WorkingLine,
} from "@/components/tools/stats-clinical/data-grid";
import { t } from "@/lib/i18n/interpolate";
import type { SmrResult } from "@/lib/tool-kit/stats-clinical/smr";
import {
  apaBounded,
  type Formatter,
  fixed,
  formatP,
} from "@/lib/tool-kit/stats-research/format";

/*
 * The result panel of the standardized mortality ratio calculator. Strings are the
 * widget's own.
 */

type Strings = typeof STANDARDIZED_MORTALITY_RATIO_CALCULATOR_STRINGS;

function ratio(fmt: Formatter, value: number): string {
  return Number.isFinite(value) ? fixed(fmt, value, 2) : "—";
}

function count(fmt: Formatter, value: number): string {
  return fmt(value, { maximumFractionDigits: 2 });
}

function verdictKey(
  result: SmrResult
): "verdictLower" | "verdictHigher" | "verdictUncertain" {
  if (result.upper < 1) {
    return "verdictLower";
  }
  return result.lower > 1 ? "verdictHigher" : "verdictUncertain";
}

export function SmrSummary({
  result,
  strings,
  fmt,
}: {
  result: SmrResult;
  strings: Strings;
  fmt: Formatter;
}) {
  const level = fmt(result.level * 100, { maximumFractionDigits: 1 });
  const interval = `${ratio(fmt, result.lower)} – ${ratio(fmt, result.upper)}`;
  const report = t(strings.report, {
    smr: ratio(fmt, result.smr),
    level,
    lower: ratio(fmt, result.lower),
    upper: ratio(fmt, result.upper),
    observed: fmt(result.observed),
    expected: count(fmt, result.expected),
    p:
      result.p < 0.001
        ? `< ${apaBounded(fmt, 0.001, 3)}`
        : `= ${apaBounded(fmt, result.p, 3)}`,
  });
  return (
    <div aria-live="polite" className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.cardSmr}
          value={ratio(fmt, result.smr)}
        />
        <StatCard label={t(strings.cardInterval, { level })} value={interval} />
        <StatCard label={strings.cardP} value={formatP(fmt, result.p)} />
      </StatGrid>
      <p className="text-foreground text-sm leading-relaxed">
        {t(strings[verdictKey(result)], {
          smr: ratio(fmt, result.smr),
          times100: fmt(result.smr * 100, { maximumFractionDigits: 0 }),
          observed: fmt(result.observed),
          expected: count(fmt, result.expected),
        })}
      </p>
      <DataGrid
        head={[strings.columnMeasure, strings.columnValue]}
        rows={[
          [strings.rowObserved, fmt(result.observed)],
          [strings.rowExpected, count(fmt, result.expected)],
          [strings.rowSmr, ratio(fmt, result.smr)],
          [
            strings.rowSmr100,
            fmt(result.smr * 100, { maximumFractionDigits: 1 }),
          ],
          [t(strings.rowInterval, { level }), interval],
          [strings.rowExcess, fixed(fmt, result.excess, 2)],
        ]}
      />
      {result.strata.length > 1 ? (
        <section className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.strataHeading}</h3>
          <DataGrid
            head={[
              strings.columnStratum,
              strings.columnObserved,
              strings.columnPersonYears,
              strings.columnRate,
              strings.columnExpected,
              strings.columnRatio,
            ]}
            rows={[
              ...result.strata.map((s) => [
                s.label,
                fmt(s.observed),
                fmt(s.personYears, { maximumFractionDigits: 1 }),
                fmt(s.rate, { maximumFractionDigits: 4 }),
                fixed(fmt, s.expected, 2),
                ratio(fmt, s.ratio),
              ]),
              [
                strings.total,
                fmt(result.observed),
                fmt(
                  result.strata.reduce((sum, s) => sum + s.personYears, 0),
                  { maximumFractionDigits: 1 }
                ),
                "",
                fixed(fmt, result.expected, 2),
                ratio(fmt, result.smr),
              ],
            ]}
          />
        </section>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={report}
        />
        <span className="font-mono text-muted-foreground text-xs">
          {report}
        </span>
      </div>
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
        <WorkingLine>{strings.workingExpected}</WorkingLine>
        <WorkingLine>
          {t(strings.workingSmr, {
            observed: fmt(result.observed),
            expected: fixed(fmt, result.expected, 3),
            smr: fixed(fmt, result.smr, 3),
          })}
        </WorkingLine>
        <p className="text-muted-foreground text-sm">{strings.workingNote}</p>
      </div>
    </div>
  );
}
